import React from 'react';
import { X } from 'lucide-react';
import {
  getMitigatingControls,
  getCounteringDefenses,
  getKevForTechnique,
  getEntity,
} from '../../lib/selectors';
import { normaliseAttackId } from '../../lib/attack-ids';
import MappedEntities from './MappedEntities';
import KevBadge from './KevBadge';

// Threat detail card (prototype .detail). Shows the technique, then its three
// coverage chains from the graph — mitigating controls (NIST↔ATT&CK), D3FEND
// countermeasures (separate), and KEV CVEs — each with provenance.
const TTPDetails = ({ ttpId, details, renderText, onClose }) => {
  if (!ttpId) return null;
  const anchorId = normaliseAttackId(ttpId);

  // Fall back to graph entity data when curated detail is absent (e.g. ⌘K navigation).
  if (!details) {
    const entity = getEntity(anchorId);
    details = entity
      ? {
          technique_name: entity.name,
          tactic: Array.isArray(entity.metadata.tactics) ? entity.metadata.tactics.join(' · ') : '',
          technique_desc: '',
          technique_detection: '',
        }
      : null;
    if (!details) return null;
  }

  const mitigatingControls = getMitigatingControls(anchorId).map(({ control, via }) => ({
    id: control.id, name: control.name, source: via.source, confidence: via.confidence,
  }));
  const counteringDefenses = getCounteringDefenses(anchorId).map(({ countermeasure, via }) => ({
    id: countermeasure.id, name: countermeasure.name, source: via.source, confidence: via.confidence,
  }));
  const kev = getKevForTechnique(anchorId);
  const kevItems = kev.map(({ cve, via }) => ({ id: cve.id, name: cve.name, source: via.source, confidence: via.confidence }));
  const kevSnapshot = kev.length ? kev[0].cve.metadata.snapshotDate : null;

  return (
    <div className="tv-detail">
      <div className="tv-dh">
        <span className="tv-did" style={{ color: 'var(--tv-text)' }}>{ttpId}</span>
        <h3>{renderText ? renderText(details.technique_name) : details.technique_name}</h3>
        <span style={{ marginLeft: 'auto', display: 'flex', gap: 10, alignItems: 'center' }}>
          {kevItems.length > 0 && <KevBadge />}
          {onClose && (
            <button className="tv-close" onClick={onClose} aria-label="Close details">
              <X style={{ width: 18, height: 18 }} />
            </button>
          )}
        </span>
      </div>
      <div className="tv-dtac">{renderText ? renderText(details.tactic) : details.tactic}</div>
      {details.technique_desc && (
        <p className="tv-desc">{renderText ? renderText(details.technique_desc) : details.technique_desc}</p>
      )}

      {details.technique_detection && (
        <div className="tv-sec">
          <div className="tv-st">Detection</div>
          <p className="tv-desc" style={{ margin: 0 }}>
            {renderText ? renderText(details.technique_detection) : details.technique_detection}
          </p>
        </div>
      )}

      <MappedEntities
        title="Mitigating controls"
        items={mitigatingControls}
        chipClass="def"
        emptyHint="No mapped controls in the loaded datasets for this technique."
      />

      <MappedEntities
        title="D3FEND countermeasures"
        items={counteringDefenses}
        chipClass="def"
        emptyHint="No D3FEND countermeasures mapped for this technique."
      />

      {kevItems.length > 0 && (
        <div className="tv-sec">
          <div className="tv-st">
            <KevBadge /> Known exploited vulnerabilities · snapshot {kevSnapshot}
          </div>
          <div className="tv-chips">
            {kevItems.map((c) => (
              <span key={c.id} className="tv-chip risk">{c.id}</span>
            ))}
          </div>
          <div className="tv-prov">
            <b>{kevItems[0].source}</b>
            <span className="tv-conf auth">Authoritative</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default TTPDetails;
